import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
interface Product {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl?: string;
}
interface Purchase {
  id: string;
  name: string;
  city: string;
  product: Product;
}
const BUYER_NAMES = ['Carlos M.', 'María G.', 'José R.', 'Ana L.', 'Miguel S.', 'Laura P.', 'David T.', 'Carmen V.', 'Roberto F.', 'Sofia C.', 'Fernando H.', 'Isabel N.', 'Diego A.', 'Patricia B.', 'Ricardo M.', 'Valentina R.', 'Andrés K.', 'Gabriela D.', 'Luis E.', 'Daniela O.', 'Jorge W.', 'Camila Z.', 'Pablo Q.', 'Natalia X.', 'Alejandro Y.'];
const US_CITIES = ['Miami, FL', 'Los Angeles, CA', 'Houston, TX', 'New York, NY', 'Chicago, IL', 'Phoenix, AZ', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Portland, OR', 'Las Vegas, NV', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD', 'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Fresno, CA'];
export function RecentPurchaseToast() {
  const [currentPurchase, setCurrentPurchase] = useState<Purchase | null>(null);
  const [recentNames, setRecentNames] = useState<string[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const location = useLocation();
  const isCheckout = location.pathname === '/checkout' || location.pathname === '/checkout-2';
  // Set fixed products on mount (no DOM scanning)
  useEffect(() => {
    setProducts([{
      title: 'AI Web Closer System',
      editionLabel: 'Mega Bundle',
      priceNumber: 81,
      priceText: '$81',
      thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
    }, {
      title: 'AI Web Closer System',
      editionLabel: 'Curso + Paquetes',
      priceNumber: 63,
      priceText: '$63',
      thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
    }, {
      title: 'AI Web Closer System',
      editionLabel: 'Curso Completo',
      priceNumber: 27,
      priceText: '$27',
      thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
    }]);
  }, []);
  // Generate random purchase
  const generatePurchase = (): Purchase => {
    // Select name (avoid recent ones)
    let availableNames = BUYER_NAMES.filter(name => !recentNames.includes(name));
    if (availableNames.length === 0) {
      availableNames = BUYER_NAMES;
    }
    const name = availableNames[Math.floor(Math.random() * availableNames.length)];
    // Select random city
    const city = US_CITIES[Math.floor(Math.random() * US_CITIES.length)];
    // Select product with weighted distribution
    let product: Product;
    if (products.length === 0) {
      // Fallback with weighted distribution: 70% $81, 20% $63, 10% $27
      const rand = Math.random();
      if (rand < 0.7) {
        product = {
          title: 'AI Web Closer System',
          editionLabel: 'Mega Bundle',
          priceNumber: 81,
          priceText: '$81'
        };
      } else if (rand < 0.9) {
        product = {
          title: 'AI Web Closer System',
          editionLabel: 'Curso + Paquetes',
          priceNumber: 63,
          priceText: '$63'
        };
      } else {
        product = {
          title: 'AI Web Closer System',
          editionLabel: 'Curso Completo',
          priceNumber: 27,
          priceText: '$27'
        };
      }
    } else {
      // Weighted selection: 70% = $81, 20% = $63, 10% = $27
      const rand = Math.random();
      const price81 = products.find(p => p.priceNumber === 81);
      const price63 = products.find(p => p.priceNumber === 63);
      const price27 = products.find(p => p.priceNumber === 27);
      if (rand < 0.7 && price81) {
        product = price81;
      } else if (rand < 0.9 && price63) {
        product = price63;
      } else if (price27) {
        product = price27;
      } else {
        product = products[Math.floor(Math.random() * products.length)];
      }
    }
    return {
      id: Date.now().toString(),
      name,
      city,
      product
    };
  };
  // Show notifications
  useEffect(() => {
    if (products.length === 0) return;
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // First notification appears immediately (500ms for render)
    const firstTimeout = setTimeout(() => {
      const purchase = generatePurchase();
      setCurrentPurchase(purchase);
      setRecentNames(prev => [...prev.slice(-9), purchase.name]);
      // Hide after 4 seconds
      setTimeout(() => {
        setCurrentPurchase(null);
      }, 4000);
    }, 500);
    // Recurring notifications every 8-15 seconds
    const scheduleNext = () => {
      const delay = 8000 + Math.random() * 7000; // 8-15 seconds
      return setTimeout(() => {
        const purchase = generatePurchase();
        setCurrentPurchase(purchase);
        setRecentNames(prev => [...prev.slice(-9), purchase.name]);
        // Hide after 4 seconds
        setTimeout(() => {
          setCurrentPurchase(null);
        }, 4000);
        // Schedule next
        const nextTimeout = scheduleNext();
        return () => clearTimeout(nextTimeout);
      }, delay);
    };
    const recurringTimeout = setTimeout(() => {
      scheduleNext();
    }, 500 + 4000); // After first notification completes
    return () => {
      clearTimeout(firstTimeout);
      clearTimeout(recurringTimeout);
    };
  }, [products]);
  return <AnimatePresence data-id="element-623">
      {currentPurchase && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className={`fixed left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 z-[60] pointer-events-none ${isCheckout ? 'bottom-24 md:bottom-28' : 'bottom-4 md:bottom-6'}`} data-id="element-624">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-3 md:p-4 flex items-center gap-3 md:gap-4 w-full md:min-w-[320px] md:max-w-md" data-id="element-625">
            {/* Product Image */}
            {currentPurchase.product.thumbnailUrl && <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-navy rounded-lg overflow-hidden" data-id="element-626">
                <img src={currentPurchase.product.thumbnailUrl} alt={currentPurchase.product.title} className="w-full h-full object-cover" data-id="element-627" />
              </div>}

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-628">
              <div className="flex items-start gap-2 mb-1" data-id="element-629">
                <CheckCircleIcon className="h-4 w-4 md:h-5 md:w-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-630" />
                <div className="flex-1 min-w-0" data-id="element-631">
                  <p className="font-body text-xs md:text-sm text-gray-900 font-semibold" data-id="element-632">
                    {currentPurchase.name} en {currentPurchase.city}
                  </p>
                  <p className="font-body text-[11px] md:text-xs text-gray-600 mt-0.5" data-id="element-633">
                    compró {currentPurchase.product.editionLabel}
                  </p>
                  <p className="font-heading text-xs md:text-sm font-bold text-gold mt-1" data-id="element-634">
                    {currentPurchase.product.priceText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}